import { binaryDxfBufferToASCIIString, isBinaryDxf } from './binary-dxf-buffer-to-ascii-string.function';
import { readDxfInsertionUnits } from './dxf-drawing-units.function';

type DxfUploadFile = Pick<File, 'name' | 'size' | 'slice'>;
const HEADER_CHUNK_BYTES = 64 * 1024;

/** Local header inspection only. Most DXFs need one 64KB read, regardless of
 * geometry size. Missing/unitless units remain unknown; no file is converted. */
export async function readDxfUploadUnits(file: DxfUploadFile): Promise<number | null> {
    let end = Math.min(file.size, HEADER_CHUNK_BYTES);
    while (end > 0) {
        const buffer = await file.slice(0, end).arrayBuffer();
        const decoded = new TextDecoder().decode(buffer);
        let text: string;
        try {
            text = isBinaryDxf(decoded) ? binaryDxfBufferToASCIIString(buffer, true) : decoded;
        } catch (error) {
            // A binary field may straddle the chunk. Retry with more header bytes.
            if (end === file.size) throw error;
            end = Math.min(file.size, end * 2);
            continue;
        }
        const section = /(?:^|\r?\n)\s*0\r?\n\s*SECTION\s*\r?\n\s*2\r?\n\s*([^\r\n]+)/i.exec(text);
        if (section && section[1].trim().toUpperCase() !== 'HEADER') return null;
        const endSection = /(?:^|\r?\n)\s*0\r?\n\s*ENDSEC\s*(?:\r?\n|$)/i.exec(text);
        if (endSection || end === file.size) {
            if (!section) return null;
            const header = text.slice(section.index, endSection?.index ?? text.length);
            return readDxfInsertionUnits(header);
        }
        end = Math.min(file.size, end * 2);
    }
    return null;
}

/** Inspect every DXF in a batch before the first upload; skip all other files.
 * Sequential header reads keep batch memory bounded and preserve file order. */
export async function findMillimeterDxfFiles(files: readonly DxfUploadFile[]): Promise<string[]> {
    const millimeterFiles: string[] = [];
    for (const file of files) {
        if (!/\.dxf$/i.test(file.name)) continue;
        try {
            if (await readDxfUploadUnits(file) === 4) millimeterFiles.push(file.name);
        } catch {
            throw new Error(`Could not check DXF units for ${file.name}. No files were uploaded.`);
        }
    }
    return millimeterFiles;
}
